import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { uploadMedia } from "@/lib/media";
import useManufacturers from "@/hooks/useManufacturers";
import useProductTypes from "@/hooks/useProductTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Package, Pencil, Star, ImagePlus, FileText, X, Search, CheckSquare, Trash2, Check } from "lucide-react";

export function ProductFormDialog({ open, onOpenChange, initial, categories, onSaved }) {
  const isEdit = !!initial?.id;
  const mfrs = useManufacturers();
  const ptypes = useProductTypes();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const seed = { ...initial };
      ["features", "applications", "accessories"].forEach((k) => {
        if (Array.isArray(seed[k])) seed[k] = seed[k].join("\n");
      });
      setForm(seed);
    } else {
      setForm({ is_active: true, show_on_website: true, show_in_quotation: true, featured: false, display_order: 0, gst_pct: 18, currency: "INR", images: [] });
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const tops = categories.filter((c) => !c.parent_id && c.is_active !== false);
  const subs = categories.filter((c) => c.parent_id === form.category_id && c.is_active !== false);
  const typesForCompany = ptypes.filter((t) => t.is_active !== false && (!form.manufacturer_id || t.manufacturer_id === form.manufacturer_id));

  const onImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const m = await uploadMedia(f);
        uploaded.push(m.url);
      }
      set("images", [...(form.images || []), ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch (err) { toast.error(fmtError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const onBrochure = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const m = await uploadMedia(file);
      set("brochure_url", m.url);
      set("brochure_name", m.filename);
      toast.success("Brochure uploaded");
    } catch (err) { toast.error(fmtError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!(form.name || "").trim()) { toast.error("Product name required"); return; }
    if (!form.manufacturer_id) { toast.error("Pick a company — every product must be linked to a company"); return; }
    if (!form.category_id) { toast.error("Pick a category — every product must be linked to a category"); return; }
    setSaving(true);
    try {
      const toList = (v) => (typeof v === "string" ? v.split("\n").map((s) => s.trim()).filter(Boolean) : (v || []));
      const payload = {
        name: form.name.trim(),
        sku: form.sku || null,
        manufacturer_id: form.manufacturer_id,
        product_type_id: form.product_type_id || null,
        category_id: form.category_id,
        subcategory_id: form.subcategory_id || null,
        category: categories.find((c) => c.id === form.category_id)?.name || form.category || null,
        short_description: form.short_description || null,
        description: form.description || null,
        specifications: form.specifications || null,
        images: form.images || [],
        brochure_url: form.brochure_url || null,
        uom: form.uom || null,
        unit_price: form.unit_price !== undefined && form.unit_price !== "" ? parseFloat(String(form.unit_price).replace(/[^0-9.]/g, "")) || null : null,
        currency: form.currency || "INR",
        gst_pct: Number(form.gst_pct) || 18,
        display_order: Number(form.display_order) || 0,
        featured: !!form.featured,
        show_on_website: form.show_on_website !== false,
        show_in_quotation: form.show_in_quotation !== false,
        is_active: form.is_active !== false,
        features: toList(form.features),
        applications: toList(form.applications),
        accessories: toList(form.accessories),
        roi_benefits: form.roi_benefits || null,
      };
      if (isEdit) await api.patch(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">{isEdit ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs font-semibold">Product name *</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs font-semibold">Product code / SKU</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} data-testid="product-sku-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">HSN Code</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.hsn || ""} onChange={(e) => set("hsn", e.target.value)} placeholder="e.g. 90314900" data-testid="product-hsn-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs font-semibold">Company *</Label>
              <Select value={form.manufacturer_id || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, manufacturer_id: v === "__none" ? "" : v, product_type_id: "" }))}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="product-company-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— Select —</SelectItem>
                  {mfrs.filter((m) => m.is_active !== false).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Product type</Label>
              <Select value={form.product_type_id || "__none"} onValueChange={(v) => set("product_type_id", v === "__none" ? "" : v)}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="product-type-select"><SelectValue placeholder={typesForCompany.length ? "Pick" : "No types"} /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— None —</SelectItem>
                  {typesForCompany.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Category *</Label>
              <Select value={form.category_id || "__none"} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v === "__none" ? "" : v, subcategory_id: "" }))}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="product-category-select"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— Select —</SelectItem>
                  {tops.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold">Subcategory</Label>
              <Select value={form.subcategory_id || "__none"} onValueChange={(v) => set("subcategory_id", v === "__none" ? "" : v)} disabled={!subs.length}>
                <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="product-subcategory-select"><SelectValue placeholder={subs.length ? "Pick" : "None"} /></SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— None —</SelectItem>
                  {subs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">Short description</Label>
            <Input className="rounded-2xl h-11 mt-1.5" value={form.short_description || ""} onChange={(e) => set("short_description", e.target.value)} placeholder="One-line summary for catalog cards" data-testid="product-short-desc-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Detailed description</Label>
            <Textarea className="rounded-2xl mt-1.5" rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="product-desc-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Specifications (one per line)</Label>
            <Textarea className="rounded-2xl mt-1.5 font-mono text-xs" rows={3} value={form.specifications || ""} onChange={(e) => set("specifications", e.target.value)} placeholder={"Frequency range: 20–100 kHz\nBattery: 8 hrs"} data-testid="product-specs-input" />
          </div>

          {/* Images + brochure */}
          <div className="p-3 rounded-2xl border border-border/60 bg-muted/20 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-[0.16em]">Media</Label>
              <span className="text-[10px] text-muted-foreground">{(form.images || []).length} image{(form.images || []).length === 1 ? "" : "s"}</span>
            </div>
            {(form.images || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {(form.images || []).map((src, i) => (
                  <div key={i} className="relative h-16 w-16 rounded-xl overflow-hidden border border-border/60">
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => set("images", form.images.filter((_, j) => j !== i))} className="absolute top-0 right-0 h-5 w-5 grid place-items-center bg-black/60 text-white rounded-bl-lg" data-testid={`product-image-remove-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-2xl border border-border/60 bg-card text-xs font-semibold cursor-pointer hover:bg-muted/30">
                <ImagePlus className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Add images"}
                <input type="file" accept="image/*" multiple className="hidden" onChange={onImages} data-testid="product-images-input" />
              </label>
              <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-2xl border border-border/60 bg-card text-xs font-semibold cursor-pointer hover:bg-muted/30">
                <FileText className="h-3.5 w-3.5" /> {form.brochure_url ? "Replace brochure" : "Upload brochure / datasheet"}
                <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" className="hidden" onChange={onBrochure} data-testid="product-brochure-input" />
              </label>
              {form.brochure_url && (
                <span className="inline-flex items-center gap-1 text-xs text-primary-700">
                  ✓ Brochure attached
                  <button type="button" className="underline text-red-600 ml-1" onClick={() => { set("brochure_url", ""); set("brochure_name", ""); }}>remove</button>
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-xs font-semibold">Unit (UOM)</Label>
              <Input className="rounded-2xl h-11 mt-1.5" value={form.uom || ""} onChange={(e) => set("uom", e.target.value)} placeholder="Nos" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Default price (₹)</Label>
              <Input type="number" className="rounded-2xl h-11 mt-1.5" value={form.unit_price ?? ""} onChange={(e) => set("unit_price", e.target.value)} data-testid="product-price-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Tax %</Label>
              <Input type="number" className="rounded-2xl h-11 mt-1.5" value={form.gst_pct ?? 18} onChange={(e) => set("gst_pct", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Display order</Label>
              <Input type="number" className="rounded-2xl h-11 mt-1.5" value={form.display_order ?? 0} onChange={(e) => set("display_order", e.target.value)} />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold">Bill of Materials / accessories (one per line)</Label>
            <Textarea className="rounded-2xl mt-1.5 font-mono text-xs" rows={3} value={form.accessories || ""} onChange={(e) => set("accessories", e.target.value)} placeholder={"Contact probe\nCarrying case\nCalibration certificate"} data-testid="product-accessories-input" />
            <p className="text-[10px] text-muted-foreground mt-1">Each row appears under the product on quotation PDFs as BOM bullets.</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {[
              { k: "featured", l: "Featured product", d: "Shown first in the catalog" },
              { k: "show_on_website", l: "Catalog visibility", d: "Visible in the Products catalog" },
              { k: "show_in_quotation", l: "Quotation visibility", d: "Selectable in new quotations" },
              { k: "is_active", l: "Active", d: "Inactive products are hidden everywhere" },
            ].map((t) => (
              <div key={t.k} className="flex items-center justify-between p-2.5 rounded-2xl border border-border/60 bg-muted/20">
                <div className="min-w-0">
                  <Label className="text-xs font-semibold">{t.l}</Label>
                  <p className="text-[10px] text-muted-foreground truncate">{t.d}</p>
                </div>
                <Switch checked={t.k === "featured" ? !!form[t.k] : form[t.k] !== false} onCheckedChange={(v) => set(t.k, v)} data-testid={`product-${t.k}-switch`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving || uploading} className="rounded-2xl" data-testid="product-save-button">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ProductManager = ({ products, categories, canManage, onChanged }) => {
  const mfrs = useManufacturers();
  const ptypes = useProductTypes();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  // Quick image upload straight from the list — no need to open Edit.
  const [imgBusy, setImgBusy] = useState("");

  const quickImage = async (p, file) => {
    if (!file) return;
    setImgBusy(p.id);
    try {
      const m = await uploadMedia(file);
      await api.patch(`/products/${p.id}`, { name: p.name, images: [...(p.images || []), m.url] });
      toast.success("Image added");
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setImgBusy(""); }
  };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} product${ids.length === 1 ? "" : "s"} permanently?\n\nOld quotations keep their snapshots and are NOT affected.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/products/bulk-delete", { ids });
      toast.success(`${data.deleted} product${data.deleted === 1 ? "" : "s"} deleted`);
      exitSelectMode();
      onChanged?.();
    } catch (err) { toast.error(fmtError(err)); } finally { setDeleting(false); }
  };

  const mfrById = mfrs.reduce((a, m) => { a[m.id] = m; return a; }, {});
  const catById = categories.reduce((a, c) => { a[c.id] = c; return a; }, {});
  const typeById = ptypes.reduce((a, t) => { a[t.id] = t; return a; }, {});
  const typesForCompany = ptypes.filter((t) => !companyFilter || t.manufacturer_id === companyFilter);
  const activeTypeFilter = typeFilter && typesForCompany.some((t) => t.id === typeFilter) ? typeFilter : "";

  const filtered = products
    .filter((p) => !companyFilter || p.manufacturer_id === companyFilter)
    .filter((p) => !activeTypeFilter || p.product_type_id === activeTypeFilter)
    .filter((p) => !catFilter || p.category_id === catFilter || p.subcategory_id === catFilter)
    .filter((p) => {
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      return (p.name || "").toLowerCase().includes(needle) || (p.sku || "").toLowerCase().includes(needle);
    })
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || (a.name || "").localeCompare(b.name || ""));

  return (
    <div data-testid="product-manager">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code…" className="pl-9 h-10 rounded-2xl bg-muted/40" data-testid="product-manager-search" />
        </div>
        <Select value={companyFilter || "__all"} onValueChange={(v) => { setCompanyFilter(v === "__all" ? "" : v); setTypeFilter(""); }}>
          <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid="product-manager-company-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All companies</SelectItem>
            {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activeTypeFilter || "__all"} onValueChange={(v) => setTypeFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid="product-manager-type-filter"><SelectValue placeholder="Product type" /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All types</SelectItem>
            {typesForCompany.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{!companyFilter && mfrById[t.manufacturer_id] ? ` · ${mfrById[t.manufacturer_id].name}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter || "__all"} onValueChange={(v) => setCatFilter(v === "__all" ? "" : v)}>
          <SelectTrigger className="rounded-2xl h-10 w-[150px]" data-testid="product-manager-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value="__all">All categories</SelectItem>
            {categories.filter((c) => !c.parent_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && !selectMode && (
          <>
            <Button variant="outline" onClick={() => setSelectMode(true)} className="rounded-2xl" data-testid="product-select-mode-btn">
              <CheckSquare className="h-4 w-4 mr-1" /> Select
            </Button>
            <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-2xl" data-testid="new-product-button">
              <Plus className="h-4 w-4 mr-1" /> New product
            </Button>
          </>
        )}
        {canManage && selectMode && (
          <>
            <Button variant="outline" onClick={() => setSelected(new Set(filtered.map((p) => p.id)))} className="rounded-2xl" data-testid="product-select-all-btn">
              Select all ({filtered.length})
            </Button>
            <Button
              variant="outline"
              onClick={deleteSelected}
              disabled={!selected.size || deleting}
              className="rounded-2xl text-red-600 border-red-200 hover:bg-red-50"
              data-testid="product-delete-selected-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" /> {deleting ? "Deleting…" : `Delete (${selected.size})`}
            </Button>
            <Button variant="ghost" onClick={exitSelectMode} className="rounded-2xl" data-testid="product-select-cancel-btn">Cancel</Button>
          </>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.map((p) => {
          const inactive = p.is_active === false;
          const isSel = selected.has(p.id);
          return (
            <Card
              key={p.id}
              className={`rounded-3xl shadow-soft ${inactive ? "opacity-60" : ""} ${canManage ? "cursor-pointer card-lift" : ""} ${isSel ? "border-red-300 ring-2 ring-red-200" : "border-border/60"}`}
              onClick={() => canManage && (selectMode ? toggleSelect(p.id) : (setEditing(p), setOpen(true)))}
              data-testid={`product-manage-card-${p.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {selectMode && (
                    <div className={`h-5 w-5 rounded-md border-2 grid place-items-center shrink-0 mt-0.5 ${isSel ? "bg-red-500 border-red-500 text-white" : "border-border"}`} data-testid={`product-select-check-${p.id}`}>
                      {isSel && <Check className="h-3.5 w-3.5" />}
                    </div>
                  )}
                  <div className="h-12 w-12 rounded-2xl bg-primary-50 grid place-items-center overflow-hidden shrink-0">
                    {(p.images || [])[0]
                      ? <img src={p.images[0]} alt="" className="h-full w-full object-cover" />
                      : <Package className="h-5 w-5 text-primary-700" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-display font-extrabold truncate">{p.name}</p>
                      {p.featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400 shrink-0" />}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {[p.sku && `Code ${p.sku}`, mfrById[p.manufacturer_id]?.name, typeById[p.product_type_id]?.name, catById[p.category_id]?.name || p.category].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {p.unit_price ? <p className="font-display font-black text-primary-700 shrink-0">{fmtINR(p.unit_price)}</p> : null}
                </div>
                <div className="flex flex-wrap gap-1 mt-2.5">
                  {inactive && <Badge variant="outline" className="text-[10px] bg-stone-100 text-stone-500 border-stone-200">Inactive</Badge>}
                  {p.show_on_website === false && <Badge variant="outline" className="text-[10px] bg-zinc-100 text-zinc-600 border-zinc-200">Hidden from catalog</Badge>}
                  {p.show_in_quotation === false && <Badge variant="outline" className="text-[10px] bg-zinc-100 text-zinc-600 border-zinc-200">Hidden from quotations</Badge>}
                  {p.brochure_url && <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">Brochure</Badge>}
                </div>
                {canManage && !selectMode && (
                  <div className="mt-2 pt-2 border-t border-border/40 flex justify-end gap-1">
                    <label
                      className="inline-flex items-center rounded-xl h-7 px-2 text-xs font-medium hover:bg-muted cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`product-quick-image-${p.id}`}
                    >
                      <ImagePlus className="h-3 w-3 mr-1" />
                      {imgBusy === p.id ? "Uploading…" : (p.images || []).length ? "Add image" : "Upload image"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={imgBusy === p.id}
                        onChange={(e) => { quickImage(p, e.target.files?.[0]); e.target.value = ""; }}
                        data-testid={`product-quick-image-input-${p.id}`}
                      />
                    </label>
                    <Button variant="ghost" size="sm" className="rounded-xl h-7 text-xs" onClick={(e) => { e.stopPropagation(); setEditing(p); setOpen(true); }} data-testid={`product-edit-${p.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <Card className="rounded-3xl border-border/60 shadow-soft sm:col-span-2"><CardContent className="p-10 text-center text-sm text-muted-foreground">No products match.</CardContent></Card>
        )}
      </div>

      <ProductFormDialog open={open} onOpenChange={setOpen} initial={editing} categories={categories} onSaved={onChanged} />
    </div>
  );
};
